import { queryOrThrow, removeChildren } from "@rootreeweb/js-lib";

import { hidePopover, showPopover } from "../popover.js";
import { processResults } from "./rendering.js";
import { SearchDataSchema } from "./types.js";

declare const RTSiteData: {
	searchApiUrl: string;
	translations: Record<string, string>;
};

const errorClass = "rt-search-popup-error";

/** The search functionality for the site. */
export default class Search {
	private debounceSendTimeout: number | undefined;

	/**
	 * Track the last submitted search query so that results don't get overwritten
	 * by slower queries
	 */
	private lastQuery = ``;

	private readonly requiredElements;

	/** Create a new search instance. */
	constructor() {
		this.requiredElements = getPageElements();
		const { closeButton, form, searchInput, searchPageForm, submitButton } =
			this.requiredElements;
		form.addEventListener("submit", (e) => {
			this.handleSubmit(e);
		});

		searchPageForm?.addEventListener("submit", (e) => {
			this.handleSubmit(e);
		});

		searchInput.addEventListener("input", () => {
			this.debounceSend();
		});

		submitButton.addEventListener("click", (e) => {
			this.handleSubmitButton(e);
		});

		closeButton.addEventListener("click", () => {
			this.hide();
		});
		window.addEventListener("resize", () => {
			this.hide();
		});
		this.loadInitialResults();
	}

	/** Debounce the search request to prevent too many requests. */
	protected debounceSend(): void {
		clearTimeout(this.debounceSendTimeout);
		this.debounceSendTimeout = window.setTimeout(() => {
			void this.send();
		}, 300);
	}

	/** Handle the error when the search request fails. */
	private handleError(): void {
		const { info, resultList, title } = this.requiredElements;
		title.textContent = "";
		removeChildren(resultList);
		resultList.classList.add(errorClass);
		resultList.textContent = "An error occurred";
		info.innerHTML = "";
	}

	/**
	 * Handle the form submission event. This converts the form submission into a
	 * get request. Typically, search would be handled on the input or change
	 * event, but this handles the case where the form is actually submitted.
	 *
	 * @param e The form submission event.
	 */
	private handleSubmit(e: SubmitEvent): void {
		if (!(e.currentTarget instanceof HTMLFormElement)) return;
		const input = queryOrThrow<HTMLInputElement>(
			e.currentTarget,
			'input[name="s"]',
		);
		e.currentTarget.action += input.value;
	}

	/**
	 * Handle the submit button click event.
	 *
	 * @param e The click event.
	 */
	private handleSubmitButton(e: MouseEvent): void {
		e.preventDefault();
		const { form, searchCompleteOverlay, searchInput } = this.requiredElements;

		const { classList } = searchCompleteOverlay;
		if (classList.contains("rt-popover-open")) {
			form.dispatchEvent(new Event("submit"));
			return;
		}

		this.show();
		searchInput.focus();
	}

	/** Hide the search overlay. */
	private hide(): void {
		const { searchCompleteOverlay, searchInput, submitButton } =
			this.requiredElements;
		hidePopover(searchCompleteOverlay);
		searchInput.classList.remove("open");
		submitButton.classList.remove("open");
	}

	/**
	 * Load the initial search results. The PHP preloads these in the dataset.
	 *
	 * @throws {Error} If the top pages are not found.
	 */
	private loadInitialResults(): void {
		const resultsRaw = this.requiredElements.resultList.dataset.top_pages;
		if (!resultsRaw) {
			throw new Error("Top pages not found");
		}
		const results = SearchDataSchema.parse(JSON.parse(resultsRaw));
		processResults(results, "init");
	}

	/** Send the search request to the server. */
	private async send(): Promise<void> {
		clearTimeout(this.debounceSendTimeout);
		const { info, resultList, searchInput, title } = this.requiredElements;
		resultList.classList.remove(errorClass);
		const term = searchInput.value;
		this.lastQuery = term;
		title.textContent = "Searching...";
		removeChildren(resultList);
		resultList.innerHTML = `<div class="rt-loader"></div>`;
		info.innerHTML = "";

		try {
			const url = new URL(RTSiteData.searchApiUrl);
			url.searchParams.append("lang", document.documentElement.lang);
			url.searchParams.append("s", term);

			const response = await fetch(url.toString());

			if (!response.ok) {
				throw new Error(`HTTP error: ${response.status}`);
			}

			const resultResponse = SearchDataSchema.parse(await response.json());

			processResults(resultResponse, this.lastQuery);
		} catch (error) {
			console.error("Search request failed:", error);
			this.handleError();
		}
	}

	/** Show the search overlay. */
	private show(): void {
		const { searchCompleteOverlay, searchInput, submitButton } =
			this.requiredElements;
		showPopover(searchCompleteOverlay, () => {
			this.hide();
		});
		searchInput.classList.add("open");
		submitButton.classList.add("open");
	}
}

/**
 * Get the page elements for the search functionality.
 *
 * @returns The page elements.
 * @throws {Error} If a required element is not found.
 */
function getPageElements() {
	return {
		closeButton: queryOrThrow("#rt-search-x-button", HTMLButtonElement),
		form: queryOrThrow("#rt-search-header-form", HTMLFormElement),
		info: queryOrThrow("#header-search-result-info", HTMLElement),
		resultList: queryOrThrow("#searchcomplete-resultslist", HTMLDivElement),
		searchCompleteOverlay: queryOrThrow(
			"#header-searchcomplete",
			HTMLDivElement,
		),

		searchInput: queryOrThrow("#rt-search-header-input", HTMLInputElement),

		searchPageForm: document.querySelector<HTMLFormElement>(
			"#rt-search-page-form",
		),

		submitButton: queryOrThrow("#rt-search-header-submit", HTMLAnchorElement),

		title: queryOrThrow("#rt-search-result-title", HTMLElement),
	};
}
